import React, { useState, useRef } from 'react';
import { ClipboardList, Pencil, Trash2, Eye } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fmtRM2, fmtDate } from '../lib/store';
import { PageHeader, StatusBadge, EmptyState, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ShareDocActions from '../components/ShareDocActions';
import { SignatureBlock, DocLogo } from '../components/DocumentDialog';
import { BrandingOverrideSection, ReferenceNumberField } from '../components/BrandingFields';
import { toast } from 'sonner';

export default function Proposals() {
  const { proposals, customers, company, getCustomer, addItem, updateItem, removeItem, generateNumber, consumeNumber } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);
  const printRef = useRef(null);

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({ number: generateNumber('proposals', today), title: '', customerId: '', date: today, status: 'draft', value: 0, summary: '' });
    setEditId(null); setOpen(true);
  };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setOpen(true); };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.title.trim() || !form.customerId) { toast.error('Title and customer are required'); return; }
    const payload = { ...form, value: Number(form.value) };
    if (editId) { updateItem('proposals', editId, payload); toast.success('Proposal updated'); }
    else { addItem('proposals', payload); consumeNumber('proposals', payload.number); toast.success('Proposal created'); }
    setOpen(false);
  };

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Proposals" subtitle="Use templates and customise for your business." actionLabel="New Proposal" onAction={openNew} />

      {proposals.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No proposals" subtitle="Create your first business proposal." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {proposals.map((p) => (
            <Card key={p.id} className="flex flex-col p-5 transition-shadow hover:shadow-md">
              <div className="mb-3 flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600"><ClipboardList className="h-5 w-5" /></div>
                <StatusBadge status={p.status} />
              </div>
              <p className="text-xs font-medium text-emerald-600">{p.number || '—'}</p>
              <h3 className="mt-0.5 font-semibold text-slate-900">{p.title}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-slate-500">{p.summary}</p>
              <p className="mt-3 text-sm text-slate-500">{getCustomer(p.customerId)?.name}</p>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
                <span className="text-lg font-bold text-slate-900">{fmtRM2(p.value)}</span>
                <div className="flex gap-0.5">
                  <button onClick={() => setPreview(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => openEdit(p)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => { removeItem('proposals', p.id); toast.success('Deleted'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? 'Edit Proposal' : 'New Proposal'}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ReferenceNumberField value={form.number} onChange={(v) => setField('number', v)} testid="proposal-number-input" />
              </div>
              <div className="sm:col-span-2"><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div>
                <Label>Customer</Label>
                <Select value={form.customerId} onValueChange={(v) => setForm({ ...form, customerId: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Value (RM)</Label><Input type="number" className="mt-1.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{['draft', 'sent', 'accepted', 'rejected'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Summary</Label><Textarea className="mt-1.5" rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <BrandingOverrideSection form={form} setField={setField} company={company} testid="proposal-branding-section" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save}>{editId ? 'Save Changes' : 'Create Proposal'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg p-0">
          <DialogHeader className="sr-only"><DialogTitle>Proposal {preview?.number || ''}</DialogTitle></DialogHeader>
          {preview && (
            <div>
            <div className="bg-white p-8" ref={printRef} data-testid="proposal-print-area">
              <DocLogo doc={preview} company={company} accentBg="bg-emerald-600" />
              <h2 className="mt-4 text-2xl font-extrabold text-emerald-700">PROPOSAL</h2>
              <p className="text-sm font-semibold text-slate-800">{preview.number || '—'}</p>
              <h3 className="mt-4 text-lg font-bold text-slate-900">{preview.title}</h3>
              <p className="text-sm text-slate-500">Prepared for {getCustomer(preview.customerId)?.name} · {fmtDate(preview.date)}</p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{preview.summary}</p>
              <div className="mt-6 flex items-center justify-between rounded-xl bg-emerald-50 p-4"><span className="text-sm font-medium text-emerald-700">Total Proposed Value</span><span className="text-2xl font-extrabold text-emerald-600">{fmtRM2(preview.value)}</span></div>
              <SignatureBlock doc={preview} company={company} />
            </div>
              <div className="flex justify-center border-t border-slate-100 bg-slate-50 py-3">
                <ShareDocActions
                  targetRef={printRef}
                  docLabel="Proposal"
                  number={preview.number}
                  recipientName={getCustomer(preview.customerId)?.contact || getCustomer(preview.customerId)?.name}
                  recipientEmail={getCustomer(preview.customerId)?.email}
                  recipientPhone={getCustomer(preview.customerId)?.phone}
                  amountText={fmtRM2(preview.value)}
                  companyName={company.name}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
